import { useState } from 'react';
import { useCompany } from '../App';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import {
  Badge, Button, Card, DataTable, ErrorBanner, Field, Flash, Input, Modal, Select, Spinner, Tabs,
} from '../components/ui';
import { date, enumLabel, localToday, percent, pricePerShare, shares } from '../lib/format';

/** Share classes, equity plans and vesting schedules — the structure issuances hang off. */
export default function Structure() {
  const { companyId, role } = useCompany();
  const [tab, setTab] = useState('classes');
  const [modal, setModal] = useState(null);
  const [flash, setFlash] = useState(null);

  const classes = useAsync(() => api.shareClasses(companyId), [companyId]);
  const plans = useAsync(() => api.equityPlans(companyId), [companyId]);
  const schedules = useAsync(() => api.vestingSchedules(companyId), [companyId]);
  const canEdit = ['LEGAL', 'ADMIN', 'OWNER'].includes(role);

  const reloadAll = () => { classes.reload(); plans.reload(); schedules.reload(); };

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>Classes &amp; plans</h1>
          <p>
            Authorized share counts are enforced on issuance under a row lock, so two concurrent
            issuances cannot together exceed a class's authorization.
          </p>
        </div>
        <div className="page-actions">
          {canEdit ? (
            <>
              <Button onClick={() => setModal({ kind: 'class' })}>New share class</Button>
              <Button onClick={() => setModal({ kind: 'plan' })}>New plan</Button>
              <Button onClick={() => setModal({ kind: 'schedule' })}>New vesting schedule</Button>
            </>
          ) : null}
        </div>
      </div>

      <Flash message={flash} onDone={() => setFlash(null)} />

      <Tabs active={tab} onChange={setTab} tabs={[
        { key: 'classes', label: `Share classes (${classes.data?.length ?? 0})` },
        { key: 'plans', label: `Equity plans (${plans.data?.length ?? 0})` },
        { key: 'schedules', label: `Vesting (${schedules.data?.length ?? 0})` },
      ]} />

      {tab === 'classes' ? (
        classes.loading ? <Spinner /> : classes.error ? <ErrorBanner error={classes.error} onRetry={classes.reload} /> : (
          <Card padded={false}>
            <DataTable
              rowKey={(r) => r.id}
              empty="No share classes yet — create one before issuing stock."
              columns={[
                { key: 'name', header: 'Class', render: (r) => <span className="primary-cell">{r.name}</span> },
                { key: 'type', header: 'Type', render: (r) => <Badge tone="neutral">{enumLabel(r.class_type)}</Badge> },
                { key: 'rank', header: 'Seniority', align: 'right', numeric: true, render: (r) => r.seniority_rank },
                { key: 'auth', header: 'Authorized', align: 'right', numeric: true, render: (r) => shares(r.authorized_shares) },
                { key: 'out', header: 'Outstanding', align: 'right', numeric: true, render: (r) => shares(r.outstanding) },
                { key: 'avail', header: 'Available', align: 'right', numeric: true, render: (r) => shares(r.available) },
                { key: 'oip', header: 'Orig. issue', align: 'right', numeric: true, render: (r) => (r.original_issue_price ? pricePerShare(r.original_issue_price) : '—') },
                { key: 'pref', header: 'Preference', render: (r) => (r.class_type === 'PREFERRED' ? `${r.liquidation_multiple}x${r.participating ? ' participating' : ''}${r.participation_cap ? ` (cap ${r.participation_cap}x)` : ''}` : '—') },
                {
                  key: 'actions', header: '',
                  render: (r) => canEdit ? (
                    <Button size="sm" variant="ghost" onClick={() => setModal({ kind: 'edit-class', classRow: r })}>
                      Edit
                    </Button>
                  ) : null,
                },
              ]}
              rows={classes.data}
            />
          </Card>
        )
      ) : null}

      {tab === 'plans' ? (
        plans.loading ? <Spinner /> : plans.error ? <ErrorBanner error={plans.error} onRetry={plans.reload} /> : (
          <Card padded={false}>
            <DataTable
              rowKey={(r) => r.id}
              empty="No equity plans yet."
              columns={[
                { key: 'name', header: 'Plan', render: (r) => <span className="primary-cell">{r.name}</span> },
                { key: 'type', header: 'Type', render: (r) => r.plan_type },
                { key: 'pool', header: 'Authorized', align: 'right', numeric: true, render: (r) => shares(r.pool_shares) },
                { key: 'consumed', header: 'Consumed', align: 'right', numeric: true, render: (r) => shares(r.consumed) },
                { key: 'unalloc', header: 'Unallocated', align: 'right', numeric: true, render: (r) => shares(r.unallocated) },
                { key: 'boardApproval', header: 'Board Approved', render: (r) => date(r.board_approval_date) },
                { key: 'over', header: '', render: (r) => (r.overGranted ? <Badge tone="warning">over by {shares(r.overGranted)}</Badge> : null) },
                {
                  key: 'actions', header: '',
                  render: (r) => canEdit ? (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <Button size="sm" variant="ghost" onClick={() => setModal({ kind: 'pool', plan: r })}>
                        Adjust pool
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setModal({ kind: 'edit-plan', planRow: r })}>
                        Edit
                      </Button>
                    </div>
                  ) : null,
                },
              ]}
              rows={plans.data}
            />
          </Card>
        )
      ) : null}

      {tab === 'schedules' ? (
        schedules.loading ? <Spinner /> : schedules.error ? <ErrorBanner error={schedules.error} onRetry={schedules.reload} /> : (
          <Card padded={false}>
            <DataTable
              rowKey={(r) => r.id}
              empty="No vesting schedules yet."
              columns={[
                { key: 'name', header: 'Schedule', render: (r) => <span className="primary-cell">{r.name}</span> },
                { key: 'kind', header: 'Kind', render: (r) => enumLabel(r.kind) },
                { key: 'total', header: 'Duration', align: 'right', render: (r) => `${r.total_months} mo` },
                { key: 'cliff', header: 'Cliff', align: 'right', render: (r) => `${r.cliff_months} mo` },
                { key: 'freq', header: 'Frequency', render: (r) => enumLabel(r.frequency) },
                { key: 'immediate', header: 'Upfront', align: 'right', render: (r) => (Number(r.immediate_fraction) ? percent(String(Number(r.immediate_fraction) * 100)) : '—') },
                { key: 'used', header: 'Grants using', align: 'right', numeric: true, render: (r) => r.grants_using },
                {
                  key: 'actions', header: '',
                  render: (r) =>
                    canEdit && Number(r.grants_using) === 0 ? (
                      <Button size="sm" variant="ghost" onClick={() => setModal({ kind: 'edit-schedule', scheduleRow: r })}>
                        Edit
                      </Button>
                    ) : null,
                },
              ]}
              rows={schedules.data}
            />
          </Card>
        )
      ) : null}

      {modal ? (
        <StructureModal
          {...modal}
          companyId={companyId}
          onClose={() => setModal(null)}
          onSaved={(msg) => { setModal(null); setFlash(msg); reloadAll(); }}
        />
      ) : null}
    </div>
  );
}

function StructureModal({ kind, plan, classRow, scheduleRow, planRow, companyId, onClose, onSaved }) {
  const today = localToday();
  const editingClass = kind === 'edit-class';
  const editingSchedule = kind === 'edit-schedule';
  const editingPlan = kind === 'edit-plan';
  const [form, setForm] = useState(() => {
    const base = {
      name: '', classType: 'COMMON', seniorityRank: '100', authorizedShares: '', parValue: '',
      originalIssuePrice: '', liquidationMultiple: '1', participating: 'false', participationCap: '',
      planType: 'MIXED', initialPoolShares: '', boardApprovalDate: '',
      totalMonths: '48', cliffMonths: '12', frequency: 'MONTHLY',
      deltaShares: '', effectiveDate: today, reason: '',
    };
    if (editingClass) {
      return {
        ...base,
        name: classRow.name, classType: classRow.class_type,
        seniorityRank: String(classRow.seniority_rank ?? 100),
        authorizedShares: classRow.authorized_shares,
        parValue: classRow.par_value ?? '',
        originalIssuePrice: classRow.original_issue_price ?? '',
        liquidationMultiple: classRow.liquidation_multiple ?? '1',
        participating: String(Boolean(classRow.participating)),
        participationCap: classRow.participation_cap ?? '',
      };
    }
    if (editingSchedule) {
      return {
        ...base,
        name: scheduleRow.name,
        totalMonths: String(scheduleRow.total_months),
        cliffMonths: String(scheduleRow.cliff_months),
        frequency: scheduleRow.frequency,
      };
    }
    if (editingPlan) {
      return {
        ...base,
        name: planRow.name,
        boardApprovalDate: planRow.board_approval_date ? String(planRow.board_approval_date).slice(0, 10) : '',
      };
    }
    return base;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const titles = {
    class: 'New share class', plan: 'New equity plan', schedule: 'New vesting schedule',
    pool: `Adjust pool — ${plan?.name}`, 'edit-class': `Edit ${classRow?.name}`,
    'edit-schedule': `Edit ${scheduleRow?.name}`, 'edit-plan': `Edit ${planRow?.name}`,
  };

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (kind === 'class') {
        const body = {
          name: form.name, classType: form.classType,
          seniorityRank: Number(form.seniorityRank), authorizedShares: form.authorizedShares,
        };
        if (form.parValue) body.parValue = form.parValue;
        if (form.classType === 'PREFERRED') {
          body.originalIssuePrice = form.originalIssuePrice;
          body.liquidationMultiple = form.liquidationMultiple;
          body.participating = form.participating === 'true';
          if (form.participating === 'true' && form.participationCap) body.participationCap = form.participationCap;
        }
        await api.createShareClass(companyId, body);
        onSaved(`Created ${form.name}.`);
      } else if (kind === 'edit-class') {
        // The patch is keyed by DB column name, not the camelCase the create
        // form uses — PATCH /share-classes/:id validates against the same
        // snake_case shape the proposal path already does (shareClassPatch).
        const body = {
          name: form.name,
          seniority_rank: Number(form.seniorityRank),
          authorized_shares: form.authorizedShares,
        };
        if (form.parValue) body.par_value = form.parValue;
        if (form.classType === 'PREFERRED') {
          body.original_issue_price = form.originalIssuePrice;
          body.liquidation_multiple = form.liquidationMultiple;
          body.participating = form.participating === 'true';
          body.participation_cap = form.participating === 'true' && form.participationCap ? form.participationCap : null;
        }
        await api.updateShareClass(companyId, classRow.id, body);
        onSaved(`Updated ${form.name}.`);
      } else if (kind === 'plan') {
        const body = { name: form.name, planType: form.planType, initialPoolShares: form.initialPoolShares };
        if (form.boardApprovalDate) body.boardApprovalDate = form.boardApprovalDate;
        await api.createEquityPlan(companyId, body);
        onSaved(`Created ${form.name}.`);
      } else if (kind === 'edit-plan') {
        // Deliberately not planType or initialPoolShares -- see updatePlan()'s
        // own comment in src/repositories/plans.js for why neither belongs here.
        const body = { name: form.name };
        if (form.boardApprovalDate) body.board_approval_date = form.boardApprovalDate;
        await api.updateEquityPlan(companyId, planRow.id, body);
        onSaved(`Updated ${form.name}.`);
      } else if (kind === 'schedule') {
        await api.createVestingSchedule(companyId, {
          name: form.name, totalMonths: Number(form.totalMonths),
          cliffMonths: Number(form.cliffMonths), frequency: form.frequency,
        });
        onSaved(`Created ${form.name}.`);
      } else if (kind === 'edit-schedule') {
        // Keyed by DB column name, like the edit-class patch above — this
        // route validates against the same shape.
        await api.updateVestingSchedule(companyId, scheduleRow.id, {
          name: form.name,
          total_months: Number(form.totalMonths),
          cliff_months: Number(form.cliffMonths),
          frequency: form.frequency,
        });
        onSaved(`Updated ${form.name}.`);
      } else {
        await api.adjustPool(companyId, plan.id, {
          deltaShares: form.deltaShares, effectiveDate: form.effectiveDate,
          reason: form.reason || undefined,
        });
        onSaved(`Adjusted ${plan.name} by ${form.deltaShares}.`);
      }
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  const fe = error?.fieldErrors ?? {};

  return (
    <Modal title={titles[kind]} onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={saving} onClick={submit}>Save</Button></>}>
      <ErrorBanner error={error} />
      <form onSubmit={submit}>
        <div className="form-grid">
          {kind !== 'pool' ? (
            <Field label="Name" required error={fe.name}>
              <Input value={form.name} onChange={set('name')} required autoFocus />
            </Field>
          ) : null}

          {kind === 'class' || editingClass ? (
            <>
              <Field label="Type" required hint={editingClass ? 'Cannot be changed once a class exists' : undefined}>
                <Select value={form.classType} onChange={set('classType')} disabled={editingClass}>
                  <option value="COMMON">Common</option>
                  <option value="PREFERRED">Preferred</option>
                </Select>
              </Field>
              <Field label="Authorized shares" required error={fe.authorizedShares}>
                <Input value={form.authorizedShares} onChange={set('authorizedShares')} inputMode="numeric" required />
              </Field>
              <Field label="Seniority rank" hint="Lower is paid first in a waterfall; common is conventionally 100">
                <Input value={form.seniorityRank} onChange={set('seniorityRank')} inputMode="numeric" />
              </Field>
              <Field label="Par value"><Input value={form.parValue} onChange={set('parValue')} inputMode="decimal" /></Field>
              {form.classType === 'PREFERRED' ? (
                <>
                  <Field label="Original issue price" required hint="Required — the waterfall needs it" error={fe.originalIssuePrice}>
                    <Input value={form.originalIssuePrice} onChange={set('originalIssuePrice')} inputMode="decimal" required />
                  </Field>
                  <Field label="Liquidation multiple">
                    <Input value={form.liquidationMultiple} onChange={set('liquidationMultiple')} inputMode="decimal" />
                  </Field>
                  <Field label="Participating">
                    <Select value={form.participating} onChange={set('participating')}>
                      <option value="false">Non-participating</option>
                      <option value="true">Participating</option>
                    </Select>
                  </Field>
                  {form.participating === 'true' ? (
                    <Field label="Participation cap" hint="As a multiple of the original issue price">
                      <Input value={form.participationCap} onChange={set('participationCap')} inputMode="decimal" />
                    </Field>
                  ) : null}
                </>
              ) : null}
            </>
          ) : null}

          {kind === 'plan' ? (
            <>
              <Field label="Plan type">
                <Select value={form.planType} onChange={set('planType')}>
                  {['ISO', 'NSO', 'RSU', 'RSA', 'MIXED'].map((t) => <option key={t} value={t}>{t}</option>)}
                </Select>
              </Field>
              <Field label="Initial pool shares" required error={fe.initialPoolShares}>
                <Input value={form.initialPoolShares} onChange={set('initialPoolShares')} inputMode="numeric" required />
              </Field>
              <Field label="Board approval date"><Input type="date" value={form.boardApprovalDate} onChange={set('boardApprovalDate')} /></Field>
            </>
          ) : null}

          {editingPlan ? (
            <Field label="Board approval date" hint="Type and pool size cannot be changed here — they have their own audit trail">
              <Input type="date" value={form.boardApprovalDate} onChange={set('boardApprovalDate')} />
            </Field>
          ) : null}

          {kind === 'schedule' || editingSchedule ? (
            <>
              <Field label="Total months" required><Input value={form.totalMonths} onChange={set('totalMonths')} inputMode="numeric" required /></Field>
              <Field label="Cliff months" required><Input value={form.cliffMonths} onChange={set('cliffMonths')} inputMode="numeric" required /></Field>
              <Field label="Frequency">
                <Select value={form.frequency} onChange={set('frequency')}>
                  {['MONTHLY', 'QUARTERLY', 'ANNUAL'].map((f) => <option key={f} value={f}>{f.toLowerCase()}</option>)}
                </Select>
              </Field>
            </>
          ) : null}

          {kind === 'pool' ? (
            <>
              <Field label="Change in shares" required hint="Negative to reduce. Recorded as a board approval, never by overwriting the original." error={fe.deltaShares}>
                <Input value={form.deltaShares} onChange={set('deltaShares')} required />
              </Field>
              <Field label="Effective date" required><Input type="date" value={form.effectiveDate} onChange={set('effectiveDate')} required /></Field>
              <Field label="Reason" hint="e.g. board consent 2026-07-14"><Input value={form.reason} onChange={set('reason')} /></Field>
            </>
          ) : null}
        </div>
      </form>
    </Modal>
  );
}
